import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import type { EmailLog } from "@/types";

const CONFLICT_MESSAGE = "Email je već na čekanju ili nema sadržaja za ponovno slanje.";

async function postAction(id: string, action: "resend" | "resolve") {
  const res = await fetch(`/api/email-logs/${id}/${action}`, { method: "POST" });
  if (res.status === 409) throw new Error(CONFLICT_MESSAGE);
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error || "Greška pri izmjeni emaila.");
  }
  return res.json();
}

export function EmailLogActions({ log }: { log: EmailLog }) {
  const queryClient = useQueryClient();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "email-logs"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  const resend = useMutation({
    mutationFn: () => postAction(log.id, "resend"),
    onSuccess: () => {
      toast.success("Email je vraćen na čekanje.");
      refresh();
    },
    onError: (err: Error) => {
      toast.error(err.message);
      refresh();
    },
  });

  const resolve = useMutation({
    mutationFn: () => postAction(log.id, "resolve"),
    onSuccess: () => {
      toast.success("Email je označen kao riješen.");
      refresh();
    },
    onError: (err: Error) => {
      toast.error(err.message);
      refresh();
    },
  });

  if (log.status !== "dead" || log.resolvedAt) return null;

  const busy = resend.isPending || resolve.isPending;

  return (
    <div className="flex justify-end gap-1">
      {log.canResend && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => resend.mutate()}>
          Pošalji ponovo
        </Button>
      )}
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => resolve.mutate()}>
        Riješeno
      </Button>
    </div>
  );
}
